import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hämtaKundförslag, kopplaKundförslag, skapaNyFrånKundförslag, Kundforslag } from '../api/client';
import { TEMA } from '../theme';

const monoStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
};

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '20px 22px',
  marginBottom: '14px',
};

const etikettStyle: React.CSSProperties = {
  ...monoStyle,
  fontSize: '10.5px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: TEMA.inkFaint,
  margin: '0 0 6px',
};

export default function NyaFortnoxKunder() {
  const [förslag, setFörslag] = useState<Kundforslag[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  const [arbetarId, setArbetarId] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    hämtaKundförslag()
      .then(res => { setFörslag(res.data); setLaddar(false); })
      .catch(err => {
        console.error('Kunde inte ladda förslagen:', err);
        setFel('Kunde inte ladda förslagen just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }, []);

  async function hantera(f: Kundforslag, åtgärd: 'koppla' | 'ny') {
    setArbetarId(f.id);
    try {
      if (åtgärd === 'koppla') await kopplaKundförslag(f.id);
      else await skapaNyFrånKundförslag(f.id);
      setFörslag(prev => prev.filter(x => x.id !== f.id));
    } catch (err) {
      console.error('Kunde inte spara:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setArbetarId(null);
    }
  }

  if (laddar) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
    </div>
  );

  if (fel) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: '#D9695F', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink }}>Nya Fortnox-kunder</h1>
        <p style={{ margin: '10px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", maxWidth: '640px' }}>
          Kunder som tillkommit i Fortnox och som liknar en prospect i Vinsales. Kopplar du ihop dem
          får prospekten sitt kundnummer, och möten, inköpare och ansvarig säljare följer med.
        </p>
      </div>

      {förslag.length === 0 && (
        <div style={{ ...kortStyle, textAlign: 'center', padding: '40px 22px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
            Inga nya kunder att stämma av just nu.
          </p>
        </div>
      )}

      {förslag.map(f => {
        const prospect = f.foreslagen_kund;
        const upptagen = arbetarId === f.id;
        return (
          <div key={f.id} style={kortStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '20px', alignItems: 'center' }}>
              <div>
                <p style={etikettStyle}>I Fortnox</p>
                <p style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink }}>{f.namn}</p>
                <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                  <span style={{ ...monoStyle, color: TEMA.accent }}>#{f.fortnox_kund_nr}</span>
                  {f.stad && ` · ${f.stad}`}
                </p>
              </div>

              <span style={{ color: TEMA.inkFaint, fontSize: '18px' }} aria-hidden="true">≈</span>

              <div>
                <p style={etikettStyle}>Prospect i Vinsales</p>
                {prospect ? (
                  <>
                    <button
                      onClick={() => navigate(`/kund/${prospect.kund_id}`)}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                        fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink,
                      }}
                    >
                      {prospect.namn}
                    </button>
                    <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                      {[prospect.stad, prospect.ansvarig_säljare || 'Ej tilldelad'].filter(Boolean).join(' · ')}
                    </p>
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: '13px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                    Prospekten har tagits bort
                  </p>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', paddingTop: '16px', borderTop: `1px solid ${TEMA.border}` }}>
              <button
                onClick={() => hantera(f, 'koppla')}
                disabled={upptagen || !prospect}
                style={{
                  background: TEMA.accent, color: TEMA.onAccent, border: `1px solid ${TEMA.accent}`,
                  borderRadius: '2px', fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500,
                  padding: '9px 18px', cursor: upptagen || !prospect ? 'default' : 'pointer',
                  opacity: upptagen || !prospect ? 0.6 : 1,
                }}
              >
                {upptagen ? 'Sparar...' : 'Samma kund – koppla ihop'}
              </button>
              <button
                onClick={() => hantera(f, 'ny')}
                disabled={upptagen}
                style={{
                  background: 'transparent', color: TEMA.inkSoft, border: `1px solid ${TEMA.border}`,
                  borderRadius: '2px', fontFamily: "'Inter', sans-serif", fontSize: '13px',
                  padding: '9px 18px', cursor: upptagen ? 'default' : 'pointer',
                }}
              >
                Olika kunder – skapa ny
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
